import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Wind, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import BoxBreathing from '@/components/BoxBreathing';
import MindOverCity from '@/components/MindOverCity';
import FocusDrive from '@/components/FocusDrive';
import PeaceDefender from '@/components/PeaceDefender';
import CalmCommand from '@/components/CalmCommand';

/**
 * The Lounge — Brotherhood+'s decompression corner for men.
 *
 * A mix of quiet tools (breathing) and light-pressure games (glide). Copy is
 * generic on purpose so we can add more tools & games over time without rewrites.
 */
export default function Lounge() {
  const { user } = useAuth();
  const nav = useNavigate();
  const gamesLocked = !!user?.trial?.locked;
  return (
    <div className="px-5 lg:px-10 pt-4 lg:pt-8 pb-16 space-y-8 lg:space-y-12" data-testid="lounge-page">
      {/* Back link */}
      <Link
        to="/app"
        className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth"
        data-testid="lounge-back"
      >
        <ArrowLeft size={14} strokeWidth={2}/> Back to Home
      </Link>

      {/* Hero — matches the "Find your quiet" treatment on Home */}
      <section className="animate-fade-up" data-testid="lounge-hero">
        <div className="relative overflow-hidden rounded-[24px] border border-navy/[0.06]">
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #20354A 0%, #15263B 60%, #0B1726 100%)' }}/>
          <div className="absolute inset-0 opacity-70" style={{ background: 'radial-gradient(460px 200px at 12% 115%, rgba(120,148,135,0.35), transparent 60%), radial-gradient(380px 170px at 96% -12%, rgba(198,165,104,0.18), transparent 60%)' }}/>
          <div className="relative p-6 lg:p-10 text-white">
            <div className="flex items-center gap-2">
              <span className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 bg-white/10 border border-white/10">
                <Wind size={20} className="text-sage" strokeWidth={1.8}/>
              </span>
              <p className="text-[11px] uppercase tracking-[0.24em] text-sage font-semibold">The Lounge</p>
            </div>
            <h1 className="font-heading text-[32px] lg:text-5xl mt-4 leading-[1.05]">
              Find your quiet.
            </h1>
            <p className="text-white/65 text-[15px] lg:text-lg mt-3 leading-relaxed max-w-2xl">
              Somewhere to slow down and blow off steam. Quiet tools when you want to catch your breath —
              simple games when you want a little friendly challenge. Nothing here is graded,
              but stakes can still make a break feel good.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-white/70">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/10">
                <Wind size={12} strokeWidth={2}/> Unwind or compete with yourself
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/10">
                More tools &amp; games coming soon
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Box Breathing (Lock In) — the calm entry point */}
      <BoxBreathing />

      {gamesLocked ? (
        <section className="max-w-3xl mx-auto" data-testid="lounge-games-locked">
          <div className="bg-navy text-white rounded-3xl p-8 lg:p-10 text-center card-soft">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mx-auto">
              <Lock size={22} className="text-gold"/>
            </div>
            <h2 className="font-heading text-2xl lg:text-3xl mt-4">The games are a member perk</h2>
            <p className="text-white/70 mt-3 max-w-md mx-auto">
              Your free trial includes breathing and the essentials. Upgrade to unlock the full Lounge —
              Mind Over City, Focus Drive, Peace Defender, and Calm Command.
            </p>
            <button onClick={() => nav('/subscription/checkout')} data-testid="lounge-upgrade-btn"
              className="mt-5 bg-gold text-navy rounded-full px-6 py-3 text-sm font-semibold hover:bg-gold/90 transition-smooth">
              Upgrade to unlock the games
            </button>
          </div>
        </section>
      ) : (
        <>
          {/* Mind Over City */}
          <section className="max-w-3xl mx-auto" data-testid="lounge-mind-over-city">
            <MindOverCity />
          </section>

          {/* Focus Drive */}
          <section className="max-w-3xl mx-auto" data-testid="lounge-focus-drive">
            <FocusDrive />
          </section>

          {/* Peace Defender — Asteroids-style neon defender */}
          <section className="max-w-3xl mx-auto" data-testid="lounge-peace-defender">
            <PeaceDefender />
          </section>

          {/* Calm Command — Missile Command-style mental wellness defender */}
          <section className="max-w-3xl mx-auto" data-testid="lounge-calm-command">
            <CalmCommand />
          </section>
        </>
      )}

      {/* Footer note */}
      <p className="text-center text-xs text-navy/40 max-w-xl mx-auto leading-relaxed">
        Nothing you do here affects your wellness score. Play hard, breathe deep,
        or just sit for a minute — the Lounge is yours.
      </p>
    </div>
  );
}
